import { AxiosError } from 'axios';
import { apiClient } from './client';

export type CameraJobStatus = 'queued' | 'dispatched' | 'running' | 'succeeded' | 'completed' | 'failed' | 'cancel_pending' | 'cancelled';
export type CameraConfigStatus = 'default' | 'pending' | 'active' | 'failed';
export type CameraExposureMode = 'auto' | 'manual';
export type CameraWhiteBalanceMode = 'auto' | 'locked' | 'manual';
export type CameraFocusMode = 'auto' | 'continuous' | 'manual' | 'fixed';

/** Canonical persisted/API camera configuration. Field names intentionally stay camelCase. */
export interface CameraConfig {
  antiBandingEnabled: boolean;
  exposureMode: CameraExposureMode;
  exposureTimeUs: number;
  analogueGain: number;
  frameDurationUs: number;
  flickerAvoidanceEnabled: boolean;
  flickerPeriodUs: number;
  whiteBalanceMode: CameraWhiteBalanceMode;
  colourGainRed: number | null;
  colourGainBlue: number | null;
  warmupSeconds: number;
  calibrationExposureTimesUs: number[];
  focusMode?: CameraFocusMode | null;
  lensPosition?: number | null;
  zoom?: number | null;
  pan?: number | null;
  tilt?: number | null;
  rotation?: 0 | 90 | 180 | 270 | null;
  horizontalFlip?: boolean | null;
  verticalFlip?: boolean | null;
  brightness?: number | null;
  contrast?: number | null;
  resolutionWidth?: number | null;
  resolutionHeight?: number | null;
  framesPerSecond?: number | null;
}

export type CameraConfigPatch = Partial<CameraConfig>;
export type CameraCapabilityValues = Record<string, unknown>;

export interface CameraRollback {
  preservedVersion?: number | null;
  reason?: string;
  recordedAt?: string;
}

export interface CameraConfigRecord {
  deviceId: string;
  configurationVersion: number;
  config: CameraConfig;
  status: CameraConfigStatus;
  persisted: boolean;
  online: boolean;
  updatedAt?: string | null;
  updatedBy?: string | null;
  acknowledgedAt?: string | null;
  lastSeenAt?: string | null;
  lastAppliedVersion?: number | null;
  lastAppliedConfig?: CameraConfig | null;
  pendingCommandId?: string | null;
  requested: CameraCapabilityValues;
  supported: CameraCapabilityValues;
  unsupported: CameraCapabilityValues;
  actual: CameraActualMetadata;
  metadata: CameraDeviceMetadata;
  error?: string | null;
  warnings?: string[];
  rollback?: CameraRollback | null;
}

export interface CameraActualMetadata {
  configurationVersion?: number | null;
  capturedAt?: string | null;
  exposureTimeUs?: number | null;
  analogueGain?: number | null;
  frameDurationUs?: number | null;
  exposureMode?: CameraExposureMode | null;
  antiBandingEnabled?: boolean | null;
  flickerAvoidanceEnabled?: boolean | null;
  flickerPeriodUs?: number | null;
  whiteBalanceMode?: CameraWhiteBalanceMode | null;
  colourGainRed?: number | null;
  colourGainBlue?: number | null;
  focusMode?: CameraFocusMode | null;
  lensPosition?: number | null;
  zoom?: number | null;
  pan?: number | null;
  tilt?: number | null;
  rotation?: number | null;
  brightness?: number | null;
  contrast?: number | null;
  resolutionWidth?: number | null;
  resolutionHeight?: number | null;
  framesPerSecond?: number | null;
  [key: string]: unknown;
}

export interface CameraDeviceMetadata {
  implementation?: string | null;
  sensorModel?: string | null;
  capturedAt?: string | null;
  [key: string]: unknown;
}

export interface CameraStatus {
  deviceId: string;
  configurationVersion: number;
  status: CameraConfigStatus;
  online: boolean;
  lastSeenAt?: string | null;
  acknowledgedAt?: string | null;
  lastAppliedVersion?: number | null;
  deliveredVersion?: number | null;
  deliveryState?: string | null;
  pendingCommandId?: string | null;
  error?: string | null;
  warnings?: string[];
  rollback?: CameraRollback | null;
}

export interface CameraMetadataResponse {
  deviceId: string;
  online: boolean;
  metadata: CameraDeviceMetadata;
  supported: CameraCapabilityValues;
  unsupported: CameraCapabilityValues;
  actual: CameraActualMetadata;
}

export interface CameraHistoryEntry {
  deviceId: string;
  configurationVersion: number;
  config: CameraConfig;
  status: CameraConfigStatus;
  updatedAt: string;
  updatedBy?: string | null;
  acknowledgedAt?: string | null;
  commandId?: string | null;
  restoredFromVersion?: number | null;
  actual?: CameraActualMetadata | null;
  supported?: CameraCapabilityValues | null;
  unsupported?: CameraCapabilityValues | null;
  errors?: string[];
  warnings?: string[];
}

export interface CameraImage {
  url: string;
  thumbnailUrl?: string | null;
  capturedAt?: string | null;
  configurationVersion?: number | null;
  metadata?: CameraActualMetadata | null;
}

export interface CameraTestCaptureJob {
  id: string;
  deviceId: string;
  configurationVersion: number;
  status: CameraJobStatus;
  current?: CameraImage | null;
  previous?: CameraImage | null;
  result?: Record<string, unknown> | null;
  error?: string | null;
  warnings?: string[];
  createdAt?: string;
  updatedAt?: string;
}

export interface CameraCalibrationCandidate {
  id: string;
  exposureTimeUs: number;
  score: number;
  recommended?: boolean;
  image: CameraImage;
  metadata?: CameraActualMetadata | null;
  config?: CameraConfigPatch;
}

export interface CameraCalibrationJob {
  id: string;
  deviceId: string;
  configurationVersion: number;
  exposureTimesUs: number[];
  status: CameraJobStatus;
  progress: number;
  candidates: CameraCalibrationCandidate[];
  recommendedCandidateId?: string | null;
  result?: Record<string, unknown> | null;
  error?: string | null;
  warnings?: string[];
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  commandId?: string;
}

export interface CameraFieldError {
  field: string;
  message: string;
}

export interface CameraApiError {
  message: string;
  fieldErrors: CameraFieldError[];
  status?: number;
}

interface RawTestCapture {
  id: string;
  device_id?: string;
  deviceId?: string;
  configuration_version?: number;
  configurationVersion?: number;
  status: CameraJobStatus;
  result?: Record<string, unknown> | null;
  error?: string | null;
  warnings?: string[];
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
}

interface RawCalibration {
  id: string;
  deviceId: string;
  configurationVersion: number;
  exposureTimesUs: number[];
  status: CameraJobStatus;
  progress: number;
  samples?: Array<Record<string, unknown>>;
  result?: Record<string, unknown> | null;
  error?: string | null;
  warnings?: string[];
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  commandId?: string;
}

function cameraPath(deviceId: string, suffix: string): string {
  return `/vision/devices/${encodeURIComponent(deviceId)}/camera${suffix}`;
}

function record(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' ? value as Record<string, unknown> : undefined;
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined;
}

function numberValue(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function booleanValue(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

export function normalizeCameraActualMetadata(value: unknown): CameraActualMetadata {
  const outer = record(value) || {};
  const source = record(outer.actual) || outer;
  const gains = source.colourGains;
  const gainRecord = record(gains);
  const gainArray = Array.isArray(gains) ? gains : [];
  const aeEnabled = booleanValue(source.aeEnabled ?? source.AeEnable);
  const awbEnabled = booleanValue(source.awbEnabled ?? source.AwbEnable);
  const awbLocked = booleanValue(source.awbLocked ?? source.AwbLocked);
  const redGain = numberValue(source.colourGainRed)
    ?? numberValue(gainRecord?.red)
    ?? numberValue(gainArray[0]);
  const blueGain = numberValue(source.colourGainBlue)
    ?? numberValue(gainRecord?.blue)
    ?? numberValue(gainArray[1]);
  const exposureMode = source.exposureMode === 'auto' || source.exposureMode === 'manual'
    ? source.exposureMode
    : aeEnabled === undefined ? undefined : aeEnabled ? 'auto' : 'manual';
  const whiteBalanceMode = ['auto', 'locked', 'manual'].includes(String(source.whiteBalanceMode))
    ? source.whiteBalanceMode as CameraWhiteBalanceMode
    : awbEnabled === undefined
      ? undefined
      : awbEnabled
        ? 'auto'
        : awbLocked ? 'locked' : redGain != null && blueGain != null ? 'manual' : 'locked';

  return {
    ...source,
    exposureTimeUs: numberValue(source.exposureTimeUs ?? source.ExposureTime),
    analogueGain: numberValue(source.analogueGain ?? source.AnalogueGain),
    frameDurationUs: numberValue(
      source.frameDurationUs ?? source.frameDuration ?? source.FrameDuration,
    ),
    exposureMode,
    whiteBalanceMode,
    colourGainRed: redGain,
    colourGainBlue: blueGain,
    resolutionWidth: numberValue(source.resolutionWidth ?? source.width ?? source.frameWidth),
    resolutionHeight: numberValue(source.resolutionHeight ?? source.height ?? source.frameHeight),
    framesPerSecond: numberValue(source.framesPerSecond ?? source.fps ?? source.frameRate),
  };
}

function captureImage(raw?: RawTestCapture): CameraImage | null {
  const result = record(raw?.result);
  const url = stringValue(result?.imageUrl) || stringValue(result?.imageURL)
    || stringValue(result?.fullImageUrl);
  if (!url) return null;
  const resultMetadata = record(result?.metadata);
  const actual = result?.actualMetadata ?? resultMetadata?.actual ?? resultMetadata;
  const metadata = normalizeCameraActualMetadata(actual);
  const configurationVersion = raw?.configuration_version ?? raw?.configurationVersion;
  return {
    url,
    capturedAt: stringValue(result?.capturedAt) || raw?.updated_at || raw?.updatedAt,
    configurationVersion,
    metadata: {
      ...metadata,
      configurationVersion: metadata.configurationVersion ?? configurationVersion,
    },
  };
}

function normalizeCapture(raw: RawTestCapture, previous?: RawTestCapture): CameraTestCaptureJob {
  return {
    id: raw.id,
    deviceId: raw.deviceId || raw.device_id || '',
    configurationVersion: raw.configurationVersion ?? raw.configuration_version ?? 0,
    status: raw.status,
    current: captureImage(raw),
    previous: captureImage(previous),
    result: raw.result,
    error: raw.error,
    warnings: raw.warnings,
    createdAt: raw.createdAt || raw.created_at,
    updatedAt: raw.updatedAt || raw.updated_at,
  };
}

function normalizeCandidate(
  sample: Record<string, unknown>,
  recommendedExposure?: number,
): CameraCalibrationCandidate | null {
  const id = stringValue(sample.id);
  if (!id) return null;
  const exposureTimeUs = Number(sample.exposureTimeUs || 0);
  const thumbnail = stringValue(sample.thumbnailUrl)
    || stringValue(sample.imageUrl)
    || (stringValue(sample.thumbnailJpegBase64)
      ? `data:image/jpeg;base64,${stringValue(sample.thumbnailJpegBase64)}`
      : '');
  return {
    id,
    exposureTimeUs,
    score: Number(sample.score || 0),
    recommended: exposureTimeUs === recommendedExposure,
    image: {
      url: stringValue(sample.imageUrl) || thumbnail,
      thumbnailUrl: thumbnail,
      metadata: normalizeCameraActualMetadata(sample.metadata),
    },
    metadata: normalizeCameraActualMetadata(sample.metadata),
    config: record(sample.config) as CameraConfigPatch | undefined,
  };
}

function normalizeCalibration(raw: RawCalibration): CameraCalibrationJob {
  const result = record(raw.result);
  const recommended = record(result?.recommended);
  const recommendedExposure = recommended ? Number(recommended.exposureTimeUs) : undefined;
  const samples = raw.samples || [];
  const candidates = samples
    .map((sample) => normalizeCandidate(record(sample) || {}, recommendedExposure))
    .filter((candidate): candidate is CameraCalibrationCandidate => candidate !== null);
  const missingIds = samples.length - candidates.length;
  const recommendedId = stringValue(result?.recommendedCandidateId)
    || stringValue(recommended?.id)
    || candidates.find((candidate) => candidate.recommended)?.id
    || null;
  return {
    ...raw,
    candidates,
    recommendedCandidateId: recommendedId,
    warnings: [
      ...(raw.warnings || []),
      ...(missingIds ? [`${missingIds} calibration samples omitted because the backend returned no candidate ID`] : []),
    ],
  };
}

function normalizeConfigRecord(raw: CameraConfigRecord): CameraConfigRecord {
  return { ...raw, actual: normalizeCameraActualMetadata(raw.actual) };
}

export function cameraApiError(error: unknown): CameraApiError {
  if (!(error instanceof AxiosError)) {
    return { message: error instanceof Error ? error.message : 'שגיאה לא צפויה', fieldErrors: [] };
  }
  const data = error.response?.data as {
    detail?: string | Array<{ loc?: Array<string | number>; msg?: string }>;
    message?: string;
    fieldErrors?: Record<string, string> | CameraFieldError[];
    errors?: CameraFieldError[];
  } | undefined;
  const fields: CameraFieldError[] = [];
  if (Array.isArray(data?.detail)) {
    data.detail.forEach((item) => fields.push({
      field: item.loc?.filter((part) => part !== 'body').join('.') || 'config',
      message: item.msg || 'ערך לא תקין',
    }));
  }
  if (Array.isArray(data?.fieldErrors)) fields.push(...data.fieldErrors);
  else if (data?.fieldErrors) {
    Object.entries(data.fieldErrors).forEach(([field, message]) => fields.push({ field, message }));
  }
  if (data?.errors) fields.push(...data.errors);
  return {
    message: typeof data?.detail === 'string'
      ? data.detail
      : data?.message || error.message || 'הבקשה נכשלה',
    fieldErrors: fields,
    status: error.response?.status,
  };
}

export async function getCameraConfig(deviceId: string): Promise<CameraConfigRecord> {
  const response = await apiClient.get<CameraConfigRecord>(cameraPath(deviceId, '/config'));
  return normalizeConfigRecord(response.data);
}

export async function patchCameraConfig(deviceId: string, config: CameraConfigPatch): Promise<CameraConfigRecord> {
  const response = await apiClient.patch<CameraConfigRecord>(cameraPath(deviceId, '/config'), config);
  return normalizeConfigRecord(response.data);
}

export async function resetCameraConfig(deviceId: string): Promise<CameraConfigRecord> {
  const response = await apiClient.post<CameraConfigRecord>(cameraPath(deviceId, '/config/reset'));
  return normalizeConfigRecord(response.data);
}

export async function reapplyCameraConfig(deviceId: string): Promise<CameraConfigRecord> {
  const response = await apiClient.post<CameraConfigRecord>(cameraPath(deviceId, '/config/reapply'));
  return normalizeConfigRecord(response.data);
}

export async function getCameraStatus(deviceId: string): Promise<CameraStatus> {
  const response = await apiClient.get<CameraStatus>(cameraPath(deviceId, '/status'));
  return response.data;
}

export async function getCameraMetadata(deviceId: string): Promise<CameraMetadataResponse> {
  const response = await apiClient.get<CameraMetadataResponse>(cameraPath(deviceId, '/metadata'));
  return { ...response.data, actual: normalizeCameraActualMetadata(response.data.actual) };
}

export async function getCameraHistory(deviceId: string, limit = 50): Promise<CameraHistoryEntry[]> {
  const response = await apiClient.get<CameraHistoryEntry[]>(cameraPath(deviceId, '/history'), {
    params: { limit },
  });
  return response.data.map((entry) => ({
    ...entry,
    actual: entry.actual ? normalizeCameraActualMetadata(entry.actual) : entry.actual,
  }));
}

export async function restoreCameraConfig(deviceId: string, version: number): Promise<CameraConfigRecord> {
  const response = await apiClient.post<CameraConfigRecord>(cameraPath(deviceId, `/history/${version}/restore`));
  return normalizeConfigRecord(response.data);
}

export async function createCameraTestCapture(
  deviceId: string,
  configurationVersion: number,
): Promise<CameraTestCaptureJob> {
  const response = await apiClient.post<RawTestCapture>(
    cameraPath(deviceId, '/test-captures'),
    { configurationVersion },
  );
  return normalizeCapture(response.data);
}

export async function listCameraTestCaptures(deviceId: string, limit = 25): Promise<CameraTestCaptureJob[]> {
  const response = await apiClient.get<RawTestCapture[]>(cameraPath(deviceId, '/test-captures'), {
    params: { limit },
  });
  return response.data.map((item, index, items) => normalizeCapture(item, items[index + 1]));
}

export async function getCameraTestCapture(deviceId: string, jobId: string): Promise<CameraTestCaptureJob> {
  const response = await apiClient.get<RawTestCapture>(
    cameraPath(deviceId, `/test-captures/${encodeURIComponent(jobId)}`),
  );
  return normalizeCapture(response.data);
}

export async function createCameraCalibration(
  deviceId: string,
  exposureTimesUs: number[],
): Promise<CameraCalibrationJob> {
  const response = await apiClient.post<RawCalibration>(
    cameraPath(deviceId, '/calibrations'),
    { exposureTimesUs },
  );
  return normalizeCalibration(response.data);
}

export async function getCameraCalibration(deviceId: string, jobId: string): Promise<CameraCalibrationJob> {
  const response = await apiClient.get<RawCalibration>(
    cameraPath(deviceId, `/calibrations/${encodeURIComponent(jobId)}`),
  );
  return normalizeCalibration(response.data);
}

export async function cancelCameraCalibration(deviceId: string, jobId: string): Promise<CameraCalibrationJob> {
  const response = await apiClient.post<RawCalibration>(
    cameraPath(deviceId, `/calibrations/${encodeURIComponent(jobId)}/cancel`),
  );
  return normalizeCalibration(response.data);
}

export async function applyCameraCalibration(
  deviceId: string,
  jobId: string,
  candidateId?: string,
): Promise<CameraConfigRecord> {
  const response = await apiClient.post<CameraConfigRecord>(
    cameraPath(deviceId, `/calibrations/${encodeURIComponent(jobId)}/apply`),
    candidateId ? { candidateId } : undefined,
  );
  return normalizeConfigRecord(response.data);
}
